// Shared booking-policy calculations — used by both the public Manage Booking
// portal (customer self-cancel / change) and the operator Refunds queue.
// Single source of truth so the customer sees the same number the operator does.

/**
 * Hours between now and pickup time. Negative if pickup is in the past.
 */
export function hoursUntilPickup(rental) {
  if (!rental?.start) return 0;
  const pickup = new Date(rental.start);
  if (isNaN(pickup)) return 0;
  return (pickup - new Date()) / (1000 * 60 * 60);
}

/**
 * Find the matching tier for a cancellation given hours-before-pickup.
 * Tiers must be sorted descending by hoursBeforePickup.
 *
 * Returns the highest tier the customer still qualifies for, or the bottom
 * tier (0h) as a fallback so we always return something.
 */
export function findCancellationTier(hoursBefore, cancellationPolicy = []) {
  const sorted = [...cancellationPolicy].sort((a, b) => b.hoursBeforePickup - a.hoursBeforePickup);
  for (const tier of sorted) {
    if (hoursBefore >= tier.hoursBeforePickup) return tier;
  }
  return sorted[sorted.length - 1] || { hoursBeforePickup: 0, refundPercent: 0 };
}

/**
 * Compute a cancellation refund preview for a rental given the company policy.
 *
 * @param {object} rental — the rental record (needs .start, .agreedPrice, .payMethod, .depositCollected)
 * @param {object} companySettings — the full settings object (uses cancellationPolicy + transactionFeePolicy)
 * @param {object} [options]
 * @param {boolean} [options.includeDeposit=true]  — also refund the deposit (Cyprus norm: yes if not used)
 * @param {'auto'|'late'|'no_show'} [options.scenario='auto'] — for which fee-policy bucket applies
 *
 * @returns {object} {
 *   originalPaid, refundEligible, refundPercent, tier,
 *   transactionFee, finalRefund, deductFee, scenario,
 * }
 */
export function calculateRefundPreview(rental, companySettings, options = {}) {
  const { includeDeposit = true, scenario = 'auto' } = options;
  const policy = companySettings?.cancellationPolicy || [];
  const feePol = companySettings?.transactionFeePolicy || {};

  // Refund what the customer ACTUALLY paid, not the inflated total.
  // After a date change with differenceOwed set, agreedPrice = new total but
  // differenceOwed is still due at desk (never paid). So the paid amount is
  // agreedPrice - differenceOwed.
  const diffOwed = parseFloat(rental?.differenceOwed) || 0;
  const agreed   = parseFloat(rental?.agreedPrice) || 0;
  const originalPaid = Math.max(0, agreed - diffOwed)
    + (includeDeposit ? (parseFloat(rental?.depositCollected) || 0) : 0);

  const hoursBefore = hoursUntilPickup(rental);
  const tier = findCancellationTier(hoursBefore, policy);
  const refundEligible = Math.round(originalPaid * tier.refundPercent) / 100;

  // Decide which fee bucket applies — explicit override or infer from hours
  const inferredScenario = scenario !== 'auto'
    ? scenario
    : (hoursBefore < 0 ? 'no_show' : (tier.refundPercent < 100 ? 'late' : 'full'));

  let deductFee = false;
  if (inferredScenario === 'no_show' || inferredScenario === 'late') deductFee = !!feePol.onLateOrNoShow;
  else if (inferredScenario === 'full')                              deductFee = !!feePol.onFullCancel;
  else if (inferredScenario === 'partial')                           deductFee = !!feePol.onPartialRefund;

  // Only deduct on online payment methods — cash refunds have no processor fee
  const onlineMethods = ['stripe', 'viva', 'paypal', 'online'];
  const isOnline = onlineMethods.includes(rental?.payMethod);
  if (!isOnline) deductFee = false;

  const feePercent = parseFloat(feePol.feePercent) || 0;
  const transactionFee = deductFee ? Math.round(refundEligible * feePercent) / 100 : 0;
  const finalRefund = Math.max(0, refundEligible - transactionFee);

  return {
    originalPaid,
    refundEligible,
    refundPercent: tier.refundPercent,
    tier,
    hoursBefore,
    deductFee,
    feePercent,
    transactionFee,
    finalRefund,
    scenario: inferredScenario,
  };
}

/**
 * Compute the refund preview for a partial refund (e.g. date change shortening).
 * Used when customer shortens stay and is owed the difference back.
 */
export function calculatePartialRefund(amountDifference, payMethod, companySettings) {
  const feePol = companySettings?.transactionFeePolicy || {};
  const feePercent = parseFloat(feePol.feePercent) || 0;
  const onlineMethods = ['stripe', 'viva', 'paypal', 'online'];
  const isOnline = onlineMethods.includes(payMethod);
  const deductFee = isOnline && !!feePol.onPartialRefund;
  const transactionFee = deductFee ? Math.round(amountDifference * feePercent) / 100 : 0;
  return {
    refundEligible: amountDifference,
    deductFee,
    feePercent,
    transactionFee,
    finalRefund: Math.max(0, amountDifference - transactionFee),
    scenario: 'partial',
  };
}

/**
 * Create a refund record (the queue entry the operator processes manually).
 * Returns the new record — caller should setRefunds(prev => [record, ...prev]).
 */
export function buildRefundRecord({ rental, amount, originalAmount, scenario, reason, payMethod, originalPaymentRef }) {
  return {
    id: 'rf-' + Date.now().toString(36).slice(-8),
    rentalId:           rental?.id || null,
    bookingRef:         rental?.bookingRef || null,
    customerId:         rental?.customerId || null,
    customer:           rental?.customer || '',
    email:              rental?.email || '',
    vehicleName:        rental?.vehicleName || '',
    vid:                rental?.vid || null,
    amount,
    originalAmount,
    payMethod:          payMethod || rental?.payMethod || 'bank',
    originalPaymentRef: originalPaymentRef || rental?.paymentRef || rental?.bookingRef || rental?.id,
    scenario:           scenario || 'full',       // 'full' | 'partial' | 'late' | 'no_show'
    reason:             reason || '',
    status:             'pending',                 // 'pending' | 'processed' | 'declined'
    createdAt:          new Date().toISOString(),
    processedAt:        null,
    processedBy:        null,
    declineReason:      null,
  };
}
